import { useState } from 'react'
import AppDropdown from '../../components/AppDropdown'
import AppFormFooterBar from '../../components/AppFormFooterBar'
import { useSupportTicketEntry } from './hooks/useSupportTicketEntry'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000'

function resolveAttachmentUrl(path) {
  if (!path) return null
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  const clean = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE_URL}${clean}`
}

export default function SupportTicketEntryScreen() {
  const { state, actions } = useSupportTicketEntry()
  const [uploadInputKey, setUploadInputKey] = useState(0)

  const heading = state.isEditMode ? 'Edit Support Ticket' : 'Submit Support Ticket'
  const subtext = state.isEditMode
    ? 'Update the details, description, or attachments on this ticket.'
    : 'Raise product queries, issues, suggestions, and feature requests.'

  return (
    <div className="mx-auto w-full max-w-[1320px] pb-28">
      <header className="mb-6">
        <h1 className="text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">{heading}</h1>
        <p className="mt-2 text-sm text-on-surface-variant">{subtext}</p>
      </header>

      <section className="rounded-2xl border border-outline-variant/20 bg-surface-container-low p-5">
        {state.isLoading ? (
          <div className="py-10 text-center text-sm text-on-surface-variant">Loading form...</div>
        ) : (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1.2fr_0.8fr]">
            {/* Left — form fields */}
            <div className="space-y-4">
              <label className="grid gap-1">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  Subject <span className="text-error">*</span>
                </span>
                <input
                  value={state.form.subject}
                  onChange={(event) => actions.setField('subject', event.target.value)}
                  className="rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2.5 text-sm text-on-surface"
                  placeholder="Example: Dashboard widgets not loading after login"
                />
                {state.fieldErrors.subject ? <span className="text-xs text-error">{state.fieldErrors.subject}</span> : null}
              </label>

              <label className="grid gap-1">
                <span className="text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                  Description <span className="text-error">*</span>
                </span>
                <textarea
                  value={state.form.description}
                  onChange={(event) => actions.setField('description', event.target.value)}
                  rows={7}
                  className="rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 py-2.5 text-sm text-on-surface"
                  placeholder="Write clear repro steps, expected vs actual behavior."
                />
                {state.fieldErrors.description ? <span className="text-xs text-error">{state.fieldErrors.description}</span> : null}
              </label>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="grid gap-1">
                  <span className="text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                    Category <span className="text-error">*</span>
                  </span>
                  <AppDropdown value={state.form.category} onChange={(value) => actions.setField('category', String(value || ''))} options={state.categoryOptions} />
                  {state.fieldErrors.category ? <span className="text-xs text-error">{state.fieldErrors.category}</span> : null}
                </label>
                <label className="grid gap-1">
                  <span className="text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">
                    Priority <span className="text-error">*</span>
                  </span>
                  <AppDropdown value={state.form.priority} onChange={(value) => actions.setField('priority', String(value || ''))} options={state.priorityOptions} />
                </label>
              </div>

              {/* Attachments */}
              <div className="rounded-lg bg-surface-container-low p-5">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <h3 className="text-lg font-bold tracking-tight text-on-surface">Documents</h3>
                  <button
                    type="button"
                    onClick={() => document.getElementById(`support-attachment-input-${state.resetKey}-${uploadInputKey}`)?.click()}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-bold text-primary hover:bg-primary/10"
                  >
                    <span className="material-symbols-outlined text-base">upload_file</span>
                    Add Files
                  </button>
                  <input
                    id={`support-attachment-input-${state.resetKey}-${uploadInputKey}`}
                    key={`support-attachment-input-${state.resetKey}-${uploadInputKey}`}
                    type="file"
                    multiple
                    className="hidden"
                    accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
                    onChange={(event) => {
                      const files = Array.from(event.target.files || [])
                      if (files.length > 0) actions.addAttachments(files)
                      setUploadInputKey((prev) => prev + 1)
                    }}
                  />
                </div>

                {/* Existing attachments (edit mode) */}
                {state.existingAttachments.length > 0 ? (
                  <div className="mb-3">
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-on-surface-variant">Saved Files</p>
                    <ul className="space-y-2">
                      {state.existingAttachments.map((att) => (
                        <li
                          key={att.id}
                          className="flex items-center justify-between gap-3 rounded-md bg-surface-container-lowest p-3 ring-1 ring-primary/20"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <span className="material-symbols-outlined rounded-full bg-primary-fixed p-2 text-primary">description</span>
                            <p className="truncate text-sm font-semibold text-on-surface">{att.originalName || 'Attachment'}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => actions.removeExistingAttachment(att)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-error"
                            aria-label="Remove saved file"
                          >
                            <span className="material-symbols-outlined text-base">close</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {/* New (staged) attachments */}
                {state.newAttachments.length > 0 ? (
                  <div>
                    {state.isEditMode ? (
                      <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-on-surface-variant">New Files</p>
                    ) : null}
                    <ul className="space-y-2">
                      {state.newAttachments.map((file, index) => (
                        <li
                          key={`${file.name}-${index}`}
                          className="flex items-center justify-between gap-3 rounded-md bg-surface-container-lowest p-3 ring-1 ring-outline-variant/20"
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <span className="material-symbols-outlined rounded-full bg-primary-fixed p-2 text-primary">description</span>
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold text-on-surface">{file.name}</p>
                              <p className="text-xs text-on-surface-variant">{Math.max(1, Math.round((file.size || 0) / 1024))} KB</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => actions.removeNewAttachment(index)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-error"
                            aria-label="Remove file"
                          >
                            <span className="material-symbols-outlined text-base">close</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {state.existingAttachments.length === 0 && state.newAttachments.length === 0 ? (
                  <div className="rounded-md bg-surface-container-lowest p-4 text-sm text-on-surface-variant">
                    No files selected. You can attach screenshots or documents.
                  </div>
                ) : null}
              </div>
            </div>

            {/* Right — requester card */}
            <aside className="h-fit rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-4">
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.14em] text-on-surface-variant">Requester</p>
              <dl className="space-y-2 text-sm">
                <div className="grid grid-cols-[90px_1fr] gap-2">
                  <dt className="font-semibold text-on-surface-variant">Name</dt>
                  <dd className="text-on-surface">{state.reporter.name || '-'}</dd>
                </div>
                <div className="grid grid-cols-[90px_1fr] gap-2">
                  <dt className="font-semibold text-on-surface-variant">Email</dt>
                  <dd className="text-on-surface">{state.reporter.email || '-'}</dd>
                </div>
              </dl>
            </aside>
          </div>
        )}
      </section>

      {/* ── Global Sticky Form Footer Bar ── */}
      <AppFormFooterBar
        cancelPath="/support/tickets/list"
        onCancel={actions.cancel}
        isSaving={state.isSaving}
        showSaveAndContinue={!state.isEditMode}
        onSaveContinue={actions.submitTicketAndContinue}
        onSaveExit={actions.submitTicket}
        continueLabel="Save & Continue"
        saveLabel={state.isEditMode ? 'Save Changes' : 'Submit Ticket'}
      />
    </div>
  )
}
